import React from 'react';
import { MultiSelect, Select, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import {
  DATE_FILTER_OPTIONS,
  KDS_AUDIT_ORDER_STATUS_LABELS,
  KITCHEN_STATUS_LABELS,
  ORDER_TYPE_LABELS,
  type DateFilterMode,
} from '../monitoring-page.constants';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type KdsAuditFiltersProps = Pick<
  UseMonitoringPage,
  | 'kdsAuditCompanyFilter' | 'setKdsAuditCompanyFilter'
  | 'kdsAuditKitchenStatusFilter' | 'setKdsAuditKitchenStatusFilter'
  | 'kdsAuditOrderTypeFilter' | 'setKdsAuditOrderTypeFilter'
  | 'kdsAuditOrderStatusFilter' | 'setKdsAuditOrderStatusFilter'
  | 'kdsAuditSessionFilter' | 'setKdsAuditSessionFilter'
  | 'kdsAuditSessionOptions'
  | 'kdsAuditSearch' | 'setKdsAuditSearch'
  | 'kdsAuditDateFilterMode' | 'setKdsAuditDateFilterMode'
  | 'kdsAuditExactDate' | 'setKdsAuditExactDate'
  | 'kdsAuditDateFrom' | 'setKdsAuditDateFrom'
  | 'kdsAuditDateTo' | 'setKdsAuditDateTo'
  | 'kdsAuditCompanyOptions'
  | 'clearKdsAuditFilters'
>;

export const KdsAuditFilters: React.FC<KdsAuditFiltersProps> = ({
  kdsAuditCompanyFilter,
  setKdsAuditCompanyFilter,
  kdsAuditKitchenStatusFilter,
  setKdsAuditKitchenStatusFilter,
  kdsAuditOrderTypeFilter,
  setKdsAuditOrderTypeFilter,
  kdsAuditOrderStatusFilter,
  setKdsAuditOrderStatusFilter,
  kdsAuditSessionFilter,
  setKdsAuditSessionFilter,
  kdsAuditSessionOptions,
  kdsAuditSearch,
  setKdsAuditSearch,
  kdsAuditDateFilterMode,
  setKdsAuditDateFilterMode,
  kdsAuditExactDate,
  setKdsAuditExactDate,
  kdsAuditDateFrom,
  setKdsAuditDateFrom,
  kdsAuditDateTo,
  setKdsAuditDateTo,
  kdsAuditCompanyOptions,
  clearKdsAuditFilters,
}) => (
  <div className={classes.filtersCard}>
    <div className={classes.filtersGridKitchen}>
      <div className={classes.fieldStack}>
        <MultiSelect
          label={'חברה'}
          placeholder={'בחר חברות'}
          data={kdsAuditCompanyOptions}
          value={kdsAuditCompanyFilter}
          onChange={setKdsAuditCompanyFilter}
          searchable
          clearable
          data-testid="monitoring-kds-company"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'סטטוס'}
          placeholder={'בחר סטטוסים'}
          data={Object.entries(KITCHEN_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          value={kdsAuditKitchenStatusFilter}
          onChange={setKdsAuditKitchenStatusFilter}
          clearable
          data-testid="monitoring-kds-kitchen-status"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'סוג הזמנה'}
          placeholder={'בחר סוגים'}
          data={Object.entries(ORDER_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          value={kdsAuditOrderTypeFilter}
          onChange={setKdsAuditOrderTypeFilter}
          clearable
          data-testid="monitoring-kds-order-type"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'סטטוס הזמנה'}
          placeholder={'בחר סטטוסים'}
          data={Object.entries(KDS_AUDIT_ORDER_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          value={kdsAuditOrderStatusFilter}
          onChange={setKdsAuditOrderStatusFilter}
          clearable
          data-testid="monitoring-kds-order-status"
        />
      </div>

      <div className={classes.fieldStack}>
        <Select
          label={'משמרת'}
          placeholder={'בחר משמרת'}
          data={kdsAuditSessionOptions}
          value={kdsAuditSessionFilter || null}
          onChange={(value) => setKdsAuditSessionFilter(value ?? '')}
          searchable
          clearable
          data-testid="monitoring-kds-session"
        />
      </div>

      <div className={classes.fieldStack}>
        <TextInput
          label={'חיפוש'}
          placeholder={'הזמנה או שולחן'}
          value={kdsAuditSearch}
          onChange={(event) => setKdsAuditSearch(event.currentTarget.value)}
          data-testid="monitoring-kds-search"
        />
      </div>

      <div className={classes.fieldStack}>
        <Select
          label={'סינון תאריך'}
          data={DATE_FILTER_OPTIONS}
          value={kdsAuditDateFilterMode}
          onChange={(value) => setKdsAuditDateFilterMode((value as DateFilterMode) ?? 'all')}
          allowDeselect={false}
          data-testid="monitoring-kds-date-mode"
        />
        {kdsAuditDateFilterMode === 'exact' && (
          <TextInput type="date" value={kdsAuditExactDate} onChange={(event) => setKdsAuditExactDate(event.currentTarget.value)} data-testid="monitoring-kds-date-exact" />
        )}
        {kdsAuditDateFilterMode === 'between' && (
          <div className={classes.fieldRow}>
            <TextInput type="date" value={kdsAuditDateFrom} onChange={(event) => setKdsAuditDateFrom(event.currentTarget.value)} data-testid="monitoring-kds-date-from" />
            <TextInput type="date" value={kdsAuditDateTo} onChange={(event) => setKdsAuditDateTo(event.currentTarget.value)} data-testid="monitoring-kds-date-to" />
          </div>
        )}
      </div>
    </div>

    <div className={classes.filtersActions}>
      <Button variant="cancel" onClick={clearKdsAuditFilters} dataTestId="monitoring-kds-clear">
        {'נקה סינונים'}
      </Button>
    </div>
  </div>
);
